import { useState, useEffect } from "react"
import { Button, Input } from "@aix/ui"
import { trpc } from "~/lib/trpc"
import toast from "react-hot-toast"

interface TrackableFormModalProps {
  isOpen: boolean
  onClose: () => void
  trackable?: any
  onSuccess: () => void
}

export default function TrackableFormModal({ isOpen, onClose, trackable, onSuccess }: TrackableFormModalProps) {
  const [name, setName] = useState("")
  const [promptText, setPromptText] = useState("")
  const [dataType, setDataType] = useState<"number" | "boolean" | "text">("text")
  const [unit, setUnit] = useState("")
  const [category, setCategory] = useState<"body" | "work" | "reflection" | "relationship" | "business">("work")
  const [checkInWindow, setCheckInWindow] = useState<"morning" | "evening">("evening")
  const [sortOrder, setSortOrder] = useState(0)
  const [isActive, setIsActive] = useState(true)

  // Validation fields
  const [minValue, setMinValue] = useState<number | null>(null)
  const [maxValue, setMaxValue] = useState<number | null>(null)
  const [minLength, setMinLength] = useState<number | null>(null)
  const [maxLength, setMaxLength] = useState<number | null>(null)

  // Load trackable data when editing
  useEffect(() => {
    if (trackable) {
      setName(trackable.name || "")
      setPromptText(trackable.promptText || "")
      setDataType(trackable.dataType || "text")
      setUnit(trackable.unit || "")
      setCategory(trackable.category || "work")
      setCheckInWindow(trackable.checkInWindow || "evening")
      setSortOrder(trackable.sortOrder || 0)
      setIsActive(trackable.isActive ?? true)
      setMinValue(trackable.minValue ? parseFloat(trackable.minValue) : null)
      setMaxValue(trackable.maxValue ? parseFloat(trackable.maxValue) : null)
      setMinLength(trackable.minLength || null)
      setMaxLength(trackable.maxLength || null)
    } else {
      // Reset form for new trackable
      setName("")
      setPromptText("")
      setDataType("text")
      setUnit("")
      setCategory("work")
      setCheckInWindow("evening")
      setSortOrder(0)
      setIsActive(true)
      setMinValue(null)
      setMaxValue(null)
      setMinLength(null)
      setMaxLength(null)
    }
  }, [trackable])

  // Mutations
  const createMutation = trpc.trackables.createTrackable.useMutation({
    onSuccess: () => {
      toast.success("Trackable created successfully!")
      onSuccess()
    },
    onError: (error) => {
      toast.error(`Failed to create: ${error.message}`)
    },
  })

  const updateMutation = trpc.trackables.updateTrackable.useMutation({
    onSuccess: () => {
      toast.success("Trackable updated successfully!")
      onSuccess()
    },
    onError: (error) => {
      toast.error(`Failed to update: ${error.message}`)
    },
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    // Validation
    if (!name || !promptText) {
      toast.error("Name and prompt text are required")
      return
    }

    if (dataType === "number" && !unit) {
      toast.error("Unit is required for number type trackables")
      return
    }

    if (trackable) {
      // Update existing - ONLY mutable fields
      updateMutation.mutate({
        trackableId: trackable.id,
        promptText,
        checkInWindow,
        sortOrder,
        isActive,
        minValue: dataType === "number" ? minValue : null,
        maxValue: dataType === "number" ? maxValue : null,
        minLength: dataType === "text" ? minLength : null,
        maxLength: dataType === "text" ? maxLength : null,
      })
    } else {
      // Create new - all fields (isActive is not in create schema, defaults to true)
      // Only include validation fields that match the dataType to avoid backend validation errors
      const createPayload: Parameters<typeof createMutation.mutate>[0] = {
        name,
        promptText,
        dataType,
        category,
        checkInWindow,
        sortOrder,
      }

      // Add type-specific fields
      if (dataType === "number") {
        createPayload.unit = unit
        if (minValue !== null) createPayload.minValue = minValue
        if (maxValue !== null) createPayload.maxValue = maxValue
      } else if (dataType === "text") {
        if (minLength !== null) createPayload.minLength = minLength
        if (maxLength !== null) createPayload.maxLength = maxLength
      }

      createMutation.mutate(createPayload)
    }
  }

  if (!isOpen) return null

  const isLoading = createMutation.isLoading || updateMutation.isLoading

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-background border-4 border-border shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="bg-secondary border-b-4 border-border p-6">
          <h2 className="text-2xl font-bold text-primary">{trackable ? "Edit Trackable" : "Create New Trackable"}</h2>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Immutability Notice (only show when editing) */}
          {trackable && (
            <div className="bg-yellow-50 border-2 border-yellow-500 p-4">
              <p className="text-sm font-semibold text-yellow-900">⚠️ Data Integrity Protection</p>
              <p className="text-xs text-yellow-800 mt-1">
                Name, Data Type, Category, and Unit cannot be edited to preserve historical data integrity. To change
                these, create a new trackable and archive this one.
              </p>
            </div>
          )}

          {/* Name */}
          <div>
            <label className="block text-sm font-semibold mb-2">
              Name <span className="text-destructive">*</span>
              {trackable && <span className="text-xs text-muted-foreground ml-2">(read-only)</span>}
            </label>
            <Input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Weight, Sleep Hours, Did Hard Thing"
              className="border-2 border-border w-full"
              required
              disabled={!!trackable}
            />
          </div>

          {/* Prompt Text */}
          <div>
            <label className="block text-sm font-semibold mb-2">
              Prompt Text <span className="text-destructive">*</span>
            </label>
            <textarea
              value={promptText}
              onChange={(e) => setPromptText(e.target.value)}
              placeholder="e.g., What was your weight this morning?"
              className="w-full border-2 border-border p-2 rounded min-h-[80px]"
              required
            />
          </div>

          {/* Data Type */}
          <div>
            <label className="block text-sm font-semibold mb-2">
              Data Type <span className="text-destructive">*</span>
              {trackable && <span className="text-xs text-muted-foreground ml-2">(read-only)</span>}
            </label>
            <select
              value={dataType}
              onChange={(e) => {
                const newType = e.target.value as "number" | "boolean" | "text"
                setDataType(newType)
                // Reset validation fields when type changes to avoid stale values
                if (newType !== "number") {
                  setMinValue(null)
                  setMaxValue(null)
                  setUnit("")
                }
                if (newType !== "text") {
                  setMinLength(null)
                  setMaxLength(null)
                }
              }}
              className="w-full border-2 border-border p-2 bg-background rounded"
              required
              disabled={!!trackable}
            >
              <option value="number">Number</option>
              <option value="boolean">Boolean (Yes/No)</option>
              <option value="text">Text</option>
            </select>
          </div>

          {/* Unit (only for number type) */}
          {dataType === "number" && (
            <div>
              <label className="block text-sm font-semibold mb-2">
                Unit <span className="text-destructive">*</span>
                {trackable && <span className="text-xs text-muted-foreground ml-2">(read-only)</span>}
              </label>
              <Input
                type="text"
                value={unit}
                onChange={(e) => setUnit(e.target.value)}
                placeholder="e.g., lbs, hours, minutes"
                className="border-2 border-border w-full"
                required
                disabled={!!trackable}
              />
            </div>
          )}

          {/* Validation: Min/Max Value (only for number type) */}
          {dataType === "number" && (
            <div className="bg-secondary border-2 border-border p-4 space-y-4">
              <h3 className="text-sm font-bold text-foreground mb-2">📊 Number Validation (Optional)</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold mb-2">Minimum Value</label>
                  <Input
                    type="number"
                    value={minValue ?? ""}
                    onChange={(e) => setMinValue(e.target.value === "" ? null : parseFloat(e.target.value))}
                    placeholder="e.g., 0"
                    className="border-2 border-border w-full"
                    step="any"
                  />
                  <p className="text-xs text-muted-foreground mt-1">Minimum allowed value</p>
                </div>
                <div>
                  <label className="block text-sm font-semibold mb-2">Maximum Value</label>
                  <Input
                    type="number"
                    value={maxValue ?? ""}
                    onChange={(e) => setMaxValue(e.target.value === "" ? null : parseFloat(e.target.value))}
                    placeholder="e.g., 12"
                    className="border-2 border-border w-full"
                    step="any"
                  />
                  <p className="text-xs text-muted-foreground mt-1">Maximum allowed value</p>
                </div>
              </div>
            </div>
          )}

          {/* Validation: Min/Max Length (only for text type) */}
          {dataType === "text" && (
            <div className="bg-secondary border-2 border-border p-4 space-y-4">
              <h3 className="text-sm font-bold text-foreground mb-2">📝 Text Validation (Optional)</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold mb-2">Minimum Length</label>
                  <Input
                    type="number"
                    value={minLength ?? ""}
                    onChange={(e) => setMinLength(e.target.value === "" ? null : parseInt(e.target.value))}
                    placeholder="e.g., 10"
                    className="border-2 border-border w-full"
                    min="0"
                  />
                  <p className="text-xs text-muted-foreground mt-1">Minimum characters required</p>
                </div>
                <div>
                  <label className="block text-sm font-semibold mb-2">Maximum Length</label>
                  <Input
                    type="number"
                    value={maxLength ?? ""}
                    onChange={(e) => setMaxLength(e.target.value === "" ? null : parseInt(e.target.value))}
                    placeholder="e.g., 500"
                    className="border-2 border-border w-full"
                    min="0"
                  />
                  <p className="text-xs text-muted-foreground mt-1">Maximum characters allowed</p>
                </div>
              </div>
            </div>
          )}

          {/* Category */}
          <div>
            <label className="block text-sm font-semibold mb-2">
              Category <span className="text-destructive">*</span>
              {trackable && <span className="text-xs text-muted-foreground ml-2">(read-only)</span>}
            </label>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value as any)}
              className="w-full border-2 border-border p-2 bg-background rounded"
              required
              disabled={!!trackable}
            >
              <option value="body">💪 Body</option>
              <option value="work">💼 Work</option>
              <option value="reflection">🧠 Reflection</option>
              <option value="relationship">❤️ Relationship</option>
              <option value="business">📈 Business</option>
            </select>
          </div>

          {/* Check-in Window */}
          <div>
            <label className="block text-sm font-semibold mb-2">
              Check-in Window <span className="text-destructive">*</span>
            </label>
            <select
              value={checkInWindow}
              onChange={(e) => setCheckInWindow(e.target.value as any)}
              className="w-full border-2 border-border p-2 bg-background rounded"
              required
            >
              <option value="morning">Morning</option>
              <option value="evening">Evening</option>
            </select>
          </div>

          {/* Sort Order */}
          <div>
            <label className="block text-sm font-semibold mb-2">Sort Order</label>
            <Input
              type="number"
              value={sortOrder}
              onChange={(e) => setSortOrder(parseInt(e.target.value) || 0)}
              placeholder="0"
              className="border-2 border-border w-full"
            />
            <p className="text-xs text-muted-foreground mt-1">Lower numbers appear first in the list</p>
          </div>

          {/* Is Active */}
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="isActive"
              checked={isActive}
              onChange={(e) => setIsActive(e.target.checked)}
              className="w-4 h-4 border-2 border-border"
            />
            <label htmlFor="isActive" className="text-sm font-semibold">
              Active (show in check-ins)
            </label>
          </div>

          {/* Actions */}
          <div className="flex gap-4 pt-4 border-t-2 border-border">
            <Button
              type="button"
              onClick={onClose}
              variant="outline"
              className="border-2 border-border flex-1"
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="border-2 border-border bg-primary text-primary-foreground flex-1"
              loading={isLoading}
            >
              {trackable ? "Update Trackable" : "Create Trackable"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}
